import { useCallback, useEffect, useMemo, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from '@packages/utils';
import { ENotificationType, Switch, Tooltip } from '@idaho-aeyc/ui-kit';
import {
  useGetFamilyListQuery,
  useExportFamilyCSVMutation,
} from 'redux/apiSlice/family.slice';
import { useGetWorkshopLocationQuery } from 'redux/apiSlice/workshops.slice';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import CardContent from 'components/Base/Layout/Content/CardContent';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import PageWrapper from 'components/Base/PageWrapper';
import { TRtkErrorType } from 'data/types/generic.types';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { EFamilyFilterParams, TFamilyDataUi } from 'data/types/family.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { buildFamilyExportBody } from 'data/convertors/family.convertor';
import useGetFamilyColumnsData from './FamilyList.data';
import { familyListKey } from '../Family.data';

const FirstActionWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  gap: 8px;
  .ant-col:has(&) {
    order: -1 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .icon-question {
    font-size: 24px;
    color: #656565;
  }

  .label-text {
    font-size: 16px;
    color: #262626;
    margin: 0;
  }

  .ant-switch {
    min-width: 44px;
  }
`;

const FamilyList = () => {
  const { familyColumns } = useGetFamilyColumnsData();
  const [showOnlyActive, setShowOnlyActive] = useState(() => {
    try {
      const storedValue = localStorage.getItem('family_list_show_active_only');
      return storedValue ? JSON.parse(storedValue) : false;
    } catch {
      return false;
    }
  });

  const { t } = useTranslation({
    keyPrefix: familyListKey,
  });

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TFamilyDataUi>({
    storagePath: ETableStoragePaths.FAMILY,
    defaultColumns: familyColumns,
  });

  const { data, isFetching, isLoading, error } = useGetFamilyListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    isActiveOnly: showOnlyActive || undefined,
  });

  const { data: locationFilters } = useGetWorkshopLocationQuery();

  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  const { goWorkshopDetails } = useNavigateWithBackState();

  // /app/workshop/:id is gated by WORKSHOP_VIEW while this tab is gated by
  // FAMILY_VIEW. Only advertise a row click when the destination is reachable,
  // otherwise every row would redirect to Page Not Found.
  const canViewWorkshop = useCheckPermission(['WORKSHOP_VIEW']);

  const navigateToWorkshopDetails = (record: TFamilyDataUi) => {
    return {
      onClick: () => goWorkshopDetails(record.workshopId, t('TITLE')),
    };
  };

  const { data: tagsFilters } = useGetTagsListQuery(ETagType.REGISTRANT);

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EFamilyFilterParams.LOCATION) {
          item.filters = locationFilters;
        }
        if (item.key === EFamilyFilterParams.TAGS) {
          item.filters = tagsFilters;
        }
        return item;
      }),
    );
  }, [tagsFilters, locationFilters]);

  const handleActiveToggle = (checked: boolean) => {
    setShowOnlyActive(checked);
    localStorage.setItem(
      'family_list_show_active_only',
      JSON.stringify(checked),
    );
  };

  const [exportFamily, { isLoading: isExporting }] =
    useExportFamilyCSVMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleColumnKeys = useCallback((): string[] => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.FAMILY
    ] as TColumnsState[];
    if (columnsState?.length) {
      return columnsState
        .filter(col => col.show && col.key !== 'actions')
        .map(col => String(col.key));
    }
    return columns
      .filter(col => col.key !== 'actions')
      .map(col => String(col.key));
  }, [columns]);

  const handleExportAll = useCallback(async () => {
    try {
      const body = buildFamilyExportBody({
        filters: selectedFilters,
        sortColumn,
        visibleColumnKeys: getVisibleColumnKeys(),
        isActiveOnly: showOnlyActive || undefined,
      });
      await exportFamily(body).unwrap();
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportFamily,
    selectedFilters,
    sortColumn,
    getVisibleColumnKeys,
    showOnlyActive,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (!selectedRows.selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const resourceIds = selectedRows.selectedRowsKeys.map(key => String(key));
      const body = buildFamilyExportBody({
        filters: selectedFilters,
        sortColumn,
        visibleColumnKeys: getVisibleColumnKeys(),
        resourceIds,
        isActiveOnly: showOnlyActive || undefined,
      });
      await exportFamily(body).unwrap();
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportFamily,
    selectedRows.selectedRowsKeys,
    selectedFilters,
    sortColumn,
    getVisibleColumnKeys,
    showOnlyActive,
    showToastNotification,
  ]);

  const tableHeaderActions = useMemo(
    () => [
      <Tooltip
        key="active-filter"
        title="The active list only contains workshops that have not yet completed."
      >
        <FirstActionWrapper>
          <QuestionCircleOutlined className="icon-question" />
          <span className="label-text">Show only active workshops:</span>
          <Switch
            checked={showOnlyActive}
            onChange={handleActiveToggle}
            size="default"
            checkedChildren="Yes"
            unCheckedChildren="No"
            aria-label="Show only active workshops toggle"
            data-cy="family-list-active-workshops-switch"
          />
        </FirstActionWrapper>
      </Tooltip>,
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
        disabled={!data?.content?.length}
      />,
    ],
    [
      showOnlyActive,
      handleExportAll,
      handleExportSelected,
      isExporting,
      data?.content?.length,
    ],
  );

  return (
    <PageWrapper
      title={t('TITLE')}
      isLoading={isLoading}
      error={error as TRtkErrorType}
      emptyWrapper={{
        isEmpty,
        alt: 'Family List Empty State',
        icon: EIconNames.FAMILY,
        emptyText: t('empty-view.EMPTY_TEXT'),
      }}
    >
      <CardContent>
        <Table<TFamilyDataUi>
          headerActions={tableHeaderActions}
          rowKey={(record: TFamilyDataUi) => record.registrantId}
          onRow={canViewWorkshop ? navigateToWorkshopDetails : undefined}
          currentPage={page}
          columns={columns}
          dataSource={data?.content}
          rowClassName={canViewWorkshop ? 'pointer' : undefined}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          hasFilters={hasFilters}
          onFiltersReset={onFiltersReset}
          storagePath={ETableStoragePaths.FAMILY}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </CardContent>
    </PageWrapper>
  );
};

export default FamilyList;
